/**
 * Every destination in the web app, as data.
 *
 * This is data rather than JSX because the two lists it feeds — the navigation
 * and the router — used to be written out separately and drifted. The nav
 * carried four links; the router had routes for three. Clicking "Foods"
 * rendered the `*` fallback, "Nothing here.", and had done so for the whole
 * life of the app: a quarter of the primary navigation going nowhere is not
 * subtle, and nothing caught it, because nothing could. There was no single
 * place where the two disagreed.
 *
 * Now there is. `NAV` names destinations, `ROUTES` defines them, and
 * `routes.test.ts` asserts that every link in the entire source tree resolves
 * to something other than the fallback.
 */

import type { ReactElement } from "react";

import { Navigate } from "react-router-dom";

import { FoodEditor } from "../pages/FoodEditor";
import { FoodScan } from "../pages/FoodScan";
import { Foods } from "../pages/Foods";
import { Plan } from "../pages/Plan";
import { Prepare } from "../pages/Prepare";
import { PrepareFlow } from "../pages/PrepareFlow";
import { PreparePot, PrepareShare } from "../pages/PreparePot";
import { RecipeEditor } from "../pages/RecipeEditor";
import { Review } from "../pages/Review";
import { SharedMeal } from "../pages/SharedMeal";
import { Shopping } from "../pages/Shopping";
import { Today } from "../pages/Today";
import { Weight } from "../pages/Weight";
import { You } from "../pages/You";

export interface AppRoute {
  path: string;
  element: ReactElement;
}

/**
 * The seven primary destinations.
 *
 * The same words on both clients where both have the screen. The set before
 * this one had four and named two of them differently per platform — "Today"
 * on the phone was "Overview" on the web, "Trends" was "Diary" — which meant
 * the two apps could not be talked about in one sentence. They are one
 * product; a destination that changes its name when you pick up a different
 * device is two. (The phone has five of the seven today: Foods and Prepare are
 * web-first, and it says so in its own tab layout.)
 *
 * The reshuffle is the usability rebuild's central move. Overview and Diary
 * were one screen split in half: the ring answered "how is today going" and the
 * diary answered "what did I eat", and every act of logging bounced between
 * them. They merge into **Today**, where the numbers and the meals sit
 * together and logging happens in place.
 *
 * That frees a slot, and **Plan** takes it — the week ahead, which is the one
 * thing the app could never answer. **Review** inherits the weekly read that
 * was buried in Overview's lower half.
 *
 * **Add** was a destination and is gone. Logging no longer has a page: it
 * happens in a sheet raised over the day it writes into, which is the only
 * screen that already knows both the meal and the date. A destination for it
 * was always an apology for the sheet not existing — the route had to *ask*
 * which meal, a question the heading it was reached from had already answered.
 *
 * **Foods** takes the slot, and it is not the same screen renamed. Add was a
 * place to log; Foods is a place to create and correct the rows logging uses,
 * which is the act that had no home at all — the starred list was filed under
 * the user's timezone, the by-hand form only appeared after a failed search,
 * and a product could only be corrected through a meal you had already eaten.
 *
 * **Prepare** is the seventh, and the first about food before it is eaten:
 * cook a pot, divide it, keep it in the fridge, share it by link, eat from it
 * over the week. It lived as four modes of the add sheet — reachable only by
 * opening a meal, on the one surface built for writing instantly, and thrown
 * away with the sheet on Escape. It sits between Foods and Plan because that
 * is the order the work happens in: the foods you have, what you cooked from
 * them, the week planned around it (Shopping under Plan subtracts what Prepare
 * holds).
 *
 * Seven will not fit across a phone as equal columns; five do, and the bar
 * scrolls sideways for the rest. "Weight" and "You" are the two left off the
 * first screen, being the ones you visit least.
 */
export interface NavLinkSpec {
  to: string;
  label: string;
  /** Only "/" needs it: without it the root link is active on every route. */
  end?: boolean;
}

export const NAV: NavLinkSpec[] = [
  { to: "/", label: "Today", end: true },
  { to: "/foods", label: "Foods" },
  { to: "/prepare", label: "Prepare" },
  { to: "/plan", label: "Plan" },
  { to: "/review", label: "Review" },
  { to: "/weight", label: "Weight" },
  { to: "/you", label: "You" },
];

/**
 * The routes that render without an account.
 *
 * Exactly one, and it is separated from the rest rather than flagged inside
 * them, because "which addresses can a stranger reach" is a question that
 * should be answerable by reading a list. Everything in `ROUTES` sits behind
 * the session gate in `App`; everything here is rendered before it, with no
 * navigation around it — a page, not a destination inside a product the reader
 * may never have heard of.
 *
 * `/m/` rather than `/shared/` because the whole address is read aloud and
 * typed into phones: `calorio.app/m/7k2p9x` fits on one line of a message.
 */
export const PUBLIC_ROUTES: AppRoute[] = [{ path: "/m/:token", element: <SharedMeal /> }];

export const ROUTES: AppRoute[] = [
  { path: "/", element: <Today /> },
  // Any day, not only today. The date is in the path so a particular day can be
  // linked to and survives a refresh — backfilling yesterday's dinner is
  // ordinary use, not an edge case.
  { path: "/day/:date", element: <Today /> },
  { path: "/prepare", element: <Prepare /> },
  { path: "/plan", element: <Plan /> },
  { path: "/review", element: <Review /> },
  { path: "/weight", element: <Weight /> },
  { path: "/you", element: <You /> },

  // Not tabs — the bar shows five at a time. Each is reached from the place
  // its numbers already show: the recipe editor from the meals on Foods,
  // shopping from Plan, a pot from the fridge on Prepare.
  //
  // The pot's own pages. `new` is listed before `:id` for the reader; React
  // Router ranks a static segment above a dynamic one regardless of order. The
  // flow is one route rather than two because the draft is component state
  // and would not survive a deep link to its second step.
  { path: "/prepare/new", element: <PrepareFlow /> },
  { path: "/prepare/:id", element: <PreparePot /> },
  { path: "/prepare/:id/share", element: <PrepareShare /> },
  //
  // Goals used to be one of these and no longer is. A screen you can only reach
  // from two links, that the navigation never names, is a screen people set
  // something on and then cannot find again — so the budget and the weight goal
  // moved onto "You", with the rest of the facts about the user. The path stays
  // as a redirect: it is in browser histories and in the address bar of anyone
  // who bookmarked it.
  { path: "/goals", element: <Navigate to="/you#goals" replace /> },
  { path: "/recipes/new", element: <RecipeEditor /> },
  { path: "/recipes/:id", element: <RecipeEditor /> },
  { path: "/plan/shopping", element: <Shopping /> },

  // The library's own two: creating a food, and correcting one. Reached from a
  // row on Foods rather than from the navigation, like the recipe editor above
  // — you arrive at them holding a particular food, or holding nothing.
  { path: "/foods", element: <Foods /> },
  { path: "/foods/new", element: <FoodEditor /> },
  // Before the `:id` route only in the reading — React Router ranks a static
  // segment above a dynamic one, so "scan" and "new" win regardless of order.
  { path: "/foods/scan", element: <FoodScan /> },
  { path: "/foods/:id", element: <FoodEditor /> },

  // Both halves of the retired add flow, kept as redirects for the same reason
  // `/goals` above is one: they are in browser histories and in the address bar
  // of anyone who bookmarked them. There is nothing to redirect *to* except the
  // day, because the sheet has no address — it is raised from Today by the `+`
  // under a meal, which is the only place that knows which meal it is for.
  { path: "/add", element: <Navigate to="/" replace /> },
  { path: "/add/scan", element: <Navigate to="/" replace /> },
];
